/**
 * Identidade: data de nascimento e CPF.
 *
 * Aparece em dois lugares, e é o mesmo formulário nos dois — daí morar
 * aqui e não dentro de uma tela:
 *
 * 1. Como **portão**, logo depois do login, para quem entrou sem
 *    declarar. É o caso de quem entrou pelo Google, que não passa por
 *    formulário nenhum, e das contas criadas antes de o cadastro pedir
 *    esses dados.
 * 2. Dentro da Carteira, para quem chegou lá por outro caminho.
 *
 * O portão existe porque a alternativa era pior: sem ele, a pessoa entra,
 * vê o salão, escolhe a sala valendo e leva uma recusa do servidor —
 * descobrindo a exigência no pior momento, que é quando ela já quis
 * jogar. Pedir na entrada é mais honesto e custa os mesmos dois campos.
 *
 * O que a tela diz sobre esses dados é a verdade: são declarados, não
 * verificados. A conta nasce com `kyc_status` em PENDING, e quem move
 * para verificado é uma pessoa que olhou documento.
 */
import { type FormEvent, useState } from 'react';

import { type EstadoConta, declararIdentidade, formatarCpf } from '../lib/carteira.ts';

/** Dígitos verificadores. Prova aritmética, não prova de quem digitou. */
function cpfValido(bruto: string): boolean {
  const d = bruto.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/** Data em que a pessoa completa 18 anos hoje: o máximo aceito no campo. */
function maiorIdadeEm(): string {
  const hoje = new Date();
  const limite = new Date(hoje.getFullYear() - 18, hoje.getMonth(), hoje.getDate());
  return limite.toISOString().slice(0, 10);
}

export function FormularioIdentidade({
  token,
  titulo,
  explicacao,
  onPronto,
}: {
  token: string;
  titulo: string;
  explicacao: string;
  onPronto: (conta: EstadoConta) => void;
}) {
  const [nascimento, setNascimento] = useState('');
  const [cpf, setCpf] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const digitos = cpf.replace(/\D/g, '');
  const cpfErrado = digitos.length === 11 && !cpfValido(digitos);

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);

    const resultado = await declararIdentidade(token, nascimento, digitos);
    setEnviando(false);

    if (resultado.estado === 'ok') {
      onPronto(resultado.dado);
      return;
    }
    setErro(resultado.mensagem);
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className="card card-destaque p-6 grid gap-5">
      <div>
        <h2 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
          {titulo}
        </h2>
        <p className="mt-2 text-muted-foreground">{explicacao}</p>
      </div>

      <label className="grid gap-1.5">
        <span className="rotulo">Data de nascimento</span>
        <input
          type="date"
          required
          max={maiorIdadeEm()}
          value={nascimento}
          onChange={(e) => setNascimento(e.target.value)}
          className="w-full rounded-lg border px-4 py-3 outline-none transition"
          style={{ background: 'var(--card)' }}
        />
        <span className="text-xs text-muted-foreground">
          A plataforma só aceita maiores de 18 anos.
        </span>
      </label>

      <label className="grid gap-1.5">
        <span className="rotulo">CPF</span>
        <input
          type="text"
          inputMode="numeric"
          required
          value={cpf}
          onChange={(e) => setCpf(formatarCpf(e.target.value))}
          placeholder="000.000.000-00"
          aria-invalid={cpfErrado}
          className="w-full rounded-lg border px-4 py-3 outline-none transition"
          style={{
            background: 'var(--card)',
            borderColor: cpfErrado ? 'var(--danger)' : undefined,
          }}
        />
        <span
          className="text-xs"
          style={{ color: cpfErrado ? 'var(--danger)' : 'var(--muted-foreground)' }}
        >
          {cpfErrado
            ? 'Esse CPF não confere. Verifique os números.'
            : 'Uma conta por pessoa. É para onde o saque volta, e só para lá.'}
        </span>
      </label>

      {erro ? (
        <p role="alert" className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
          {erro}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn btn-primary !py-3"
        disabled={enviando || nascimento === '' || digitos.length !== 11 || cpfErrado}
      >
        {enviando ? 'Um instante…' : 'Continuar'}
      </button>

      <p className="text-xs text-muted-foreground">
        Estes dados são informados por você e ficam guardados uma vez só — para corrigir
        depois é preciso falar com o suporte. Ainda não há conferência de documento: quando
        houver, ela é que confirma a identidade.
      </p>
    </form>
  );
}

/**
 * O portão. Ocupa a tela inteira, e a única saída é preencher ou sair da
 * conta — de propósito: meio caminho seria a pessoa circular pelo produto
 * e esbarrar na exigência mais adiante, que é o que se quer evitar.
 */
export function PortaoIdentidade({
  token,
  onPronto,
  onSair,
}: {
  token: string;
  onPronto: (conta: EstadoConta) => void;
  onSair: () => void;
}) {
  return (
    <main className="mx-auto w-full max-w-md px-4 py-10 grid gap-5 content-start grow">
      <FormularioIdentidade
        token={token}
        titulo="Falta só isto"
        explicacao="Para jogar valendo dinheiro precisamos saber que você é maior de 18 anos, e o CPF é o que faz o saque voltar para a sua conta."
        onPronto={onPronto}
      />
      <button type="button" className="btn btn-ghost justify-self-center" onClick={onSair}>
        Sair da conta
      </button>
    </main>
  );
}
